import { t } from "i18next";
import * as z from "zod";

import {
  CUSTOMER_SEGMENTS,
  DESTINATION_EXTERNAL_TYPES,
  TRANSACTION_TYPES,
} from "@/features/shared/api/enums";

/**
 * Schéma du formulaire d'initiation d'une transaction
 * (InitiateTransactionRequestDto, guide §5.3) — montant saisi en unité
 * MAJEURE (converti en `amountMinor` au submit via `majorToMinor`).
 * Une destination est exigée : wallet interne (`destinationWalletId`) ou
 * référence externe (`destinationExternalRef`).
 *
 * Factory (règle projet) : créé via `createInitiateTransactionSchema()` et
 * mémoïsé au niveau composant sur la langue courante — jamais d'instance
 * exportée.
 */
export const createInitiateTransactionSchema = () =>
  z
    .object({
      type: z.enum(TRANSACTION_TYPES, {
        errorMap: () => ({ message: t("requiredFieldError") }),
      }),
      productCode: z
        .string()
        .trim()
        .min(1, { message: t("requiredFieldError") }),
      sourceWalletId: z
        .string()
        .trim()
        .min(1, { message: t("requiredFieldError") })
        .uuid({ message: t("invalidFieldError") }),
      destinationWalletId: z
        .string()
        .trim()
        .uuid({ message: t("invalidFieldError") })
        .or(z.literal(""))
        .optional(),
      destinationExternalType: z.enum(DESTINATION_EXTERNAL_TYPES, {
        errorMap: () => ({ message: t("requiredFieldError") }),
      }),
      destinationExternalRef: z.string().trim().or(z.literal("")).optional(),
      amount: z
        .number({ invalid_type_error: t("invalidFieldError") })
        .positive({ message: t("invalidFieldError") }),
      customerSegment: z.enum(CUSTOMER_SEGMENTS, {
        errorMap: () => ({ message: t("requiredFieldError") }),
      }),
      partyId: z
        .string()
        .trim()
        .uuid({ message: t("invalidFieldError") })
        .or(z.literal(""))
        .optional(),
      promoCode: z.string().trim().or(z.literal("")).optional(),
      customerReference: z.string().trim().or(z.literal("")).optional(),
    })
    .superRefine((values, ctx) => {
      // Destination cohérente avec le type : wallet interne si NONE, sinon
      // référence externe (compte bancaire / mobile money).
      if (
        values.destinationExternalType === "NONE" &&
        !values.destinationWalletId?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["destinationWalletId"],
          message: t("requiredFieldError"),
        });
      }
      if (
        values.destinationExternalType !== "NONE" &&
        !values.destinationExternalRef?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["destinationExternalRef"],
          message: t("requiredFieldError"),
        });
      }
    });

export type InitiateTransactionFormValues = z.infer<
  ReturnType<typeof createInitiateTransactionSchema>
>;

/**
 * Schéma du motif d'échec de `POST /Transactions/{id}/mark-failed` : le motif
 * (query param `reason`) est obligatoire.
 */
export const createMarkFailedSchema = () =>
  z.object({
    reason: z.string().trim().min(1, { message: t("requiredFieldError") }),
  });

export type MarkFailedFormValues = z.infer<ReturnType<typeof createMarkFailedSchema>>;
